import { buildSrcSet, imageUrl, isManagedImage, RESPONSIVE_WIDTHS, SIZES } from "@/lib/images";

/**
 * Image optimisée pour le web et le mobile.
 *
 * Remplace `<img>` partout où l'on affiche une image de la médiathèque, et
 * applique d'un coup les quatre réglages que Lighthouse / PageSpeed
 * réclament sur une page e-commerce :
 *
 *  1. `srcSet` + `sizes` — le navigateur télécharge la déclinaison adaptée à
 *     la place réellement occupée à l'écran (400 px sur un téléphone plutôt
 *     que 1600 px). Voir lib/images.js pour la mécanique côté backend.
 *  2. `loading="lazy"` par défaut — les images hors écran ne retardent plus
 *     le premier affichage. `priority` bascule sur un chargement immédiat +
 *     `fetchPriority="high"` : à réserver à l'image LCP (le grand visuel en
 *     haut de page), qu'il ne faut au contraire SURTOUT pas différer.
 *  3. `decoding="async"` — le décodage ne bloque pas le thread principal.
 *  4. `width`/`height` (ou un conteneur au ratio fixe) — réserve la place
 *     avant l'arrivée de l'image et évite le décalage de mise en page (CLS),
 *     l'une des trois métriques Core Web Vitals de Google.
 *
 * Une URL externe (miniature YouTube, illustration de thème) traverse le
 * composant sans transformation : on garde alors le comportement d'un `<img>`
 * ordinaire, avec le lazy loading et le décodage asynchrone en prime.
 */
export default function SmartImage({
  src,
  alt = "",
  sizes = SIZES.productGrid,
  widths = RESPONSIVE_WIDTHS,
  priority = false,
  version,
  className = "",
  ...rest
}) {
  const srcSet = buildSrcSet(src, widths, version);
  return (
    <img
      src={imageUrl(src, { version })}
      {...(srcSet ? { srcSet, sizes } : {})}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      className={className}
      {...rest}
    />
  );
}

export { isManagedImage, SIZES };
